import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Reveal from '../components/Reveal';
import AdvisoryInquiry from '../components/AdvisoryInquiry';
import { useLanguage } from '../lib/LanguageContext';
import images from '../lib/artAdvisoryImages.json';

const CONTENT = {
  en: {
    title: 'Art Advisory',
    intro: "Finding the right work of art for your space shouldn't be complicated. We make it simple.",
    paragraphs: [
      "Tell us about your space and what you're looking for and together we'll find the right solution. Whether it's a single statement piece or a larger curated selection, we work with you to match the art to the space, the mood, and the vision.",
      "If you're in Cluj-Napoca, we'll visit your space in person and install the artwork for you.",
      "Whether you're furnishing a home, an office, or a hospitality space, we work with a diverse roster of artists across styles, scales, and price points. There's something for every space and every vision.",
      'For collectors, we offer art acquisition guidance, helping you identify works that are not only right for your space, but worth investing in. Our gallery represents artists whose work holds both aesthetic and long-term value.',
    ],
    designerTitle: 'Are you an interior designer?',
    designerCopy: "We'd love to collaborate. Whether you're working on a residential or commercial project, we can help you source, select, and install contemporary art that elevates the spaces you design. Get in touch and let's talk.",
    designerLink: "Let's collaborate", ready: 'Ready to start?', clientLink: 'Tell us about your space',
    subjects: ['Art Advisory — My space', 'Art Advisory — Interior design collaboration'],
    alts: ['Contemporary painting in a living space with a lounge chair', 'Large abstract painting above a sofa', 'Contemporary painting in a living room with leather chairs'],
  },
  ro: {
    title: 'Consultanță în artă',
    intro: 'Arta potrivită transformă un spațiu. Noi te ajutăm să o găsești.',
    paragraphs: [
      'Nu există o formulă universală: fiecare spațiu are logica lui, fiecare client are o viziune diferită. De aceea lucrăm împreună cu tine: ascultăm, propunem, ajustăm. Până când totul se potrivește.',
      'Dacă ești în Cluj-Napoca, mergem mai departe de atât: venim la tine, vedem spațiul și instalăm lucrarea la fața locului.',
      'Reprezentăm artiști cu practici diverse: pictură, sculptură, tehnici mixte. Indiferent de spațiu sau buget, există o lucrare potrivită.',
      'Pentru colecționari, oferim consultanță în achiziție: te ajutăm să alegi lucrări cu valoare estetică reală și potențial de apreciere pe termen lung.',
    ],
    designerTitle: 'Ești designer de interior?',
    designerCopy: 'Colaborările cu designeri fac parte din ce facem. Dacă lucrezi la un proiect rezidențial sau comercial și cauți artă contemporană care să completeze spațiul, scrie-ne.',
    designerLink: 'Hai să colaborăm', ready: 'Ești gata să începem?', clientLink: 'Povestește-ne despre spațiul tău',
    subjects: ['Consultanță în artă — Spațiul meu', 'Consultanță în artă — Colaborare design interior'],
    alts: ['Pictură contemporană într-un interior cu fotoliu', 'Pictură abstractă de mari dimensiuni deasupra unei canapele', 'Pictură contemporană într-un living cu fotolii din piele'],
  },
};

const ArtAdvisoryPage = () => {
  const { language } = useLanguage();
  const c = CONTENT[language] || CONTENT.en;
  const [inquiry, setInquiry] = useState(null);
  const photo = (index, eager = false) => (
    <img src={images[index].src} width={images[index].width} height={images[index].height} alt={c.alts[index]} loading={eager ? 'eager' : 'lazy'} decoding="async" className="block w-full h-auto" />
  );
  return (
    <div className="bg-bone text-charcoal min-h-screen" data-testid="art-advisory-page">
      <Header />
      <main className="px-6 md:px-12 lg:px-16 pt-36 md:pt-44 pb-20 md:pb-28 max-w-[1680px] mx-auto">
        <section className="grid md:grid-cols-2 gap-10 lg:gap-20 items-center">
          <Reveal>
            <h1 className="font-extrabold text-5xl lg:text-7xl leading-[1.05]">{c.title}</h1>
            <p className="mt-8 text-2xl lg:text-3xl leading-relaxed font-light">{c.intro}</p>
            <a href="#advisory-contact" className="editorial-link inline-flex mt-8">{c.ready} <span aria-hidden="true">→</span></a>
          </Reveal>
          <div>{photo(0, true)}</div>
        </section>
        <section className="grid md:grid-cols-2 gap-10 lg:gap-20 items-center py-16 md:py-24">
          <div className="order-2 md:order-1">{photo(1)}</div>
          <Reveal className="order-1 md:order-2 space-y-7">
            {c.paragraphs.map((paragraph, index) => <p key={index} className="text-lg lg:text-xl leading-relaxed text-charcoal/80">{paragraph}</p>)}
          </Reveal>
        </section>
        <section className="grid md:grid-cols-2 gap-10 lg:gap-20 items-center border-t border-charcoal/20 pt-16 md:pt-24">
          <Reveal>
            <h2 className="font-bold text-3xl lg:text-5xl leading-tight">{c.designerTitle}</h2>
            <p className="mt-7 text-lg lg:text-xl leading-relaxed text-charcoal/80">{c.designerCopy}</p>
            <button type="button" onClick={() => setInquiry(1)} className="editorial-link inline-flex mt-8" data-testid="advisory-designer-contact">{c.designerLink} <span aria-hidden="true">→</span></button>
          </Reveal>
          <div>{photo(2)}</div>
        </section>
        <section id="advisory-contact" className="scroll-mt-28 mt-16 md:mt-24 border-t border-charcoal/20 pt-12 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <h2 className="font-bold text-4xl lg:text-6xl">{c.ready}</h2>
          <button type="button" onClick={() => setInquiry(0)} className="inline-flex self-start bg-charcoal text-bone px-7 py-4 gap-4 hover:opacity-75 transition-opacity" data-testid="advisory-client-contact">{c.clientLink} <span aria-hidden="true">→</span></button>
        </section>
      </main>
      {inquiry !== null && <AdvisoryInquiry open onOpenChange={(open) => { if (!open) setInquiry(null); }} subject={c.subjects[inquiry]} ro={language === 'ro'} />}
      <Footer />
    </div>
  );
};

export default ArtAdvisoryPage;
